import { DatePickerInput } from '#atoms';
import { useItemList } from '#pages/DashboardCustomerMembership/hooks/useItemList';
import {
  formatKoreanDate,
  getExpiryDate,
  getSelectDefaultValue
} from '#pages/DashboardCustomerMembership/utils';
import { useDashboard } from '#providers/Dashboard';
import { useScheduleManage } from '#providers/SchedulesManage';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Item,
  Label
} from 'src/components/pages/DashboardChartForm/MemberInfo/components/styles';
import Select from 'src/shared/components/select/Select';
import { useDashboardCustomerMembershipState } from '../../dahsboardCustomerMembershipState';
import { useLoadLatest } from '../../hooks/useLoadLatest';
import { Discount } from './Discount';
import ExpiryDate from './ExpiryDate';
import ExpiryPeriod from './ExpiryPeriod';
import Lecture from './Lecture';

export const DashboardCustomerMembershipForm = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  const { customerDetail } = useDashboard();

  const localStorageSelectedLecture = JSON.parse(
    localStorage.getItem('selectedLecture') || '{}'
  );

  const {
    dashboardCustomerMembershipState,
    setDashboardCustomerMembershipState
  } = useDashboardCustomerMembershipState();

  const { selectedLecture, setSelectedLecture } = useScheduleManage();

  useEffect(() => {
    if (localStorageSelectedLecture) {
      setSelectedLecture({
        lectureId: localStorageSelectedLecture.id,
        lectureName: localStorageSelectedLecture.title
      });

      setDashboardCustomerMembershipState(
        produce(draft => {
          draft.lectureId = localStorageSelectedLecture.lectureId;
        })
      );
    }
  }, []);

  const { itemList } = useItemList(customerDetail?.person?.id);

  const onSelectChange = (value: string, name: string) => {
    if (name === 'isRequestPayment') {
      setDashboardCustomerMembershipState(prev => ({
        ...prev,
        [name]: value === 'true' ? true : false
      }));

      return;
    }

    setDashboardCustomerMembershipState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (
      dashboardCustomerMembershipState.startedDate &&
      dashboardCustomerMembershipState.expiryPeriod
    ) {
      const { startedDate, expiryPeriod, expiryDate } =
        dashboardCustomerMembershipState;

      setDashboardCustomerMembershipState(
        produce(draft => {
          draft.expiryDate = getExpiryDate(
            startedDate,
            expiryPeriod,
            expiryDate
          );
        })
      );
    }
  }, [
    dashboardCustomerMembershipState.startedDate,
    dashboardCustomerMembershipState.expiryPeriod
  ]);

  useEffect(() => {
    if (!dashboardCustomerMembershipState.startedDate) {
      setDashboardCustomerMembershipState(
        produce(draft => {
          draft.startedDate = new Date().toISOString();
        })
      );
    }
  }, [dashboardCustomerMembershipState.startedDate]);

  useEffect(() => {
    setDashboardCustomerMembershipState(
      produce(draft => {
        draft.lectureId = selectedLecture.lectureId;
      })
    );
  }, [selectedLecture]);

  const { isLoad, latestMembership } = useLoadLatest(
    customerDetail?.person?.id
  );

  return (
    <div className="">
      {itemList.map(item => {
        return (
          <Item
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            key={item.view}
          >
            <Label>{item.view}</Label>

            {item.type === 'view' && item.name === 'lectureId' && (
              <Lecture selectedLecture={selectedLecture} />
            )}

            {item.type === 'date' && (
              <div className="flex gap-4 items-center">
                <DatePickerInput
                  date={new Date(dashboardCustomerMembershipState.startedDate)}
                  required
                  title="개시 날짜 선택"
                  onChange={date => {
                    const dateString = date.toISOString();
                    setDashboardCustomerMembershipState(
                      produce(draft => {
                        draft.startedDate = dateString;
                      })
                    );
                  }}
                >
                  <div className="h-[37px] px-16 py-8 bg-[#2e2a3a] rounded-lg justify-center items-center gap-1 inline-flex">
                    <div className="text-[#d7d2de] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
                      {formatKoreanDate(
                        dashboardCustomerMembershipState.startedDate
                      )}
                    </div>
                  </div>
                </DatePickerInput>
              </div>
            )}

            {item.type === 'select' && item.name === 'expiryPeriod' && (
              <ExpiryPeriod
                {...item}
                options={
                  item.options as {
                    label: string;
                    value: number | string;
                  }[]
                }
                onSelectChange={onSelectChange}
              />
            )}

            {item.type === 'view' && item.name === 'expiryDate' && (
              <ExpiryDate
                expiryDate={dashboardCustomerMembershipState.expiryDate}
                isCustom={
                  dashboardCustomerMembershipState.expiryPeriod === 'custom'
                }
                onChange={date => {
                  setDashboardCustomerMembershipState(
                    produce(draft => {
                      draft.expiryDate = date.toISOString();
                    })
                  );
                }}
              />
            )}

            {item.type === 'modal' && (
              <Discount
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
              />
            )}

            {item.type === 'select' && item.name !== 'expiryPeriod' && (
              <>
                {
                  <div className="flex flex-col gap-8">
                    <div className="flex gap-8 justify-between items-center">
                      <Select
                        defaultValue={getSelectDefaultValue({
                          name: typeof item.name === 'string' ? item.name : '',
                          latestMembership,
                          isLoad
                        })}
                        options={
                          item.options
                            ? (item.options as {
                                label: string;
                                value: number | string;
                              }[])
                            : []
                        }
                        onChange={value =>
                          onSelectChange(
                            value,
                            typeof item.name === 'string' ? item.name : ''
                          )
                        }
                      />
                    </div>
                  </div>
                }
              </>
            )}
          </Item>
        );
      })}
    </div>
  );
};
